<template>
  <div class="collection-items-container space-y-2">
    <component
      :is="containerComponent.component"
      v-if="orderedCases && orderedCases.length"
      class="collection-case-list space-y-2"
      v-bind="containerComponent.props"
      @input="onCasesChange"
    >
      <collection-case-item
        v-for="collectionCase in orderedCases"
        :key="collectionCase.id"
        class="collection-item mb-1"
        :collection-case="collectionCase"
        :can-remove-cases="collectionCase.can.removableCase"
        :collection-id="collection.id"
        @removeCase="onRemoveCase"
      />
    </component>

    <empty-state v-if="!orderedCases || !orderedCases.length" :font-icon="false">
      <h5 class="text-muted">
        {{ $t('cmrad.star_adding_cases') }}
        <btn class="d-inline" fab icon icon-name="fa-ellipsis-v" size="lg" variant="light" />
        {{ $t('cmrad.star_adding_cases_2') }}
      </h5>
      <btn variant="primary" class="mb-3" href="/home" :btn-text="$t('cmrad.visit_home_start_adding')" />
      <b-img fluid :src="$loadImage('add-to-collection.gif')" />
    </empty-state>

    <div v-if="!dirty && error" class="sticky-button pin-bottom d-flex justify-content-end pb-3">
      <btn
        v-b-tooltip.hover
        fab
        variant="primary"
        :btn-text="$t('cmrad.save_failed_retry')"
        :disabled="busy"
        @click.prevent.stop="onSave"
      />
    </div>
  </div>
</template>

<script>
  import toastr from 'toastr';
  import Draggable from 'vuedraggable';
  import LoadingModal from '@/Atoms/LoadingModal';
  import CollectionCaseItem from '@/Molecules/CollectionCaseItem';
  import EmptyState from '@/Atoms/EmptyState';
  import NoResultsIcon from '@/Atoms/svgIcons/NoResultsIcon';
  import debounce from 'lodash.debounce';
  import axios from 'axios';

  const CancelToken = axios.CancelToken;
  const source = CancelToken.source();

  const doSaveChanges = debounce(async (vm, id, data) => {
    vm.busy = true;

    try {
      await vm.$api(
        'case_collections.handle_cases',
        {
          version: '1.0',
          case_collection: id,
        },
        {
          cases: data,
        },
        source.token
      );

      vm.dirty = false;
      vm.error = false;
    } catch (err) {
      console.error(err);
      vm.error = true;
    }

    vm.busy = false;
    if (!vm.dirty && vm.error) toastr.error(this.$t('cmrad.saving_data_error_message')); // prevent error to show while dragging
  }, 500);

  export default {
    name: 'CollectionCaseList',

    components: {
      Draggable,
      CollectionCaseItem,
      LoadingModal,
      EmptyState,
      NoResultsIcon,
    },

    props: {
      cases: Array,
      collection: Object,
    },

    data() {
      const defaultCases = this.cases.map((item, idx) => ({
        ...item,
        position: idx + 1,
      }));

      return {
        orderedCases: [...defaultCases],
        dirty: false,
        busy: false,
        error: false,
      };
    },

    computed: {
      casesData() {
        return this.orderedCases.map((item) => ({ case_id: item.id }));
      },

      dragOptions() {
        return {
          //forceFallback: true, // stops holding effect
          supportPointer: false,
          sort: true,
          ghostClass: 'ghost', // Class name for the drop placeholder
          dragClass: 'sortable-drag', // Class name for the dragging item
          fallbackTolerance: 1,
          removeCloneOnHide: true,
          //emptyInsertThreshold: 3,
        };
      },

      containerComponent() {
        let component = {
          component: 'div',
          props: {},
        };
        if (this.collection.can.handleCases) {
          component.component = 'draggable';

          component.props = { ...component.props, value: [...this.orderedCases], ...this.dragOptions };
        }

        return component;
      },

      uniqueId() {
        return `collection-cases-${this.collection.id}`;
      },
    },

    methods: {
      onRemoveCase(item) {
        this.orderedCases = this.orderedCases.filter((ccase) => ccase.id != item.id);
        this.dirty = true;
      },

      async onSave() {
        doSaveChanges(this, this.collection.id, this.casesData);
      },

      onCasesChange(newCases) {
        this.orderedCases = newCases.map((item, idx) => ({
          ...item,
          position: idx + 1,
        }));

        this.dirty = true;
        if (this.collection.can.handleCases && this.casesData.length) {
          doSaveChanges(this, this.collection.id, this.casesData);
        }
      },
    },
  };
</script>
